import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { tWork, tCompletion } from "@/i18n/translations";
import { LanguageSelector } from "@/components/LanguageSelector";
import { CheckCircle2, AlertTriangle, Clock, Home, ClipboardList } from "lucide-react";
export default function Confirmation() {
  const nav = useNavigate();
  const loc = useLocation();
  const { t, lang } = useLanguage();
  const d = loc.state;

  useEffect(() => {
    if (!d) nav("/urgency");
  }, [d, nav]);

  if (!d) return null;

  const urgencyLabel = d.urgency === "Urgent" ? t("urgent") : t("not_urgent");

  return (
    <div className="min-h-screen bg-background">
      <LanguageSelector />
      <div className="max-w-md mx-auto px-6 py-10">
        <div className="flex flex-col items-center text-center animate-slide-up">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary shadow-lg">
            <CheckCircle2 className="h-11 w-11 text-secondary" />
          </div>
          <h2 className="font-display text-3xl font-bold text-primary mt-6">{t("request_received")}</h2>
          <p className="text-muted-foreground mt-2">{t("thanks_touch")}</p>
        </div>

        <div className="mt-8 rounded-3xl border border-border bg-white p-6 shadow-sm space-y-5">
          <div className="text-center pb-4 border-b border-border">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">{t("job_reference")}</p>
            <p data-testid="confirmation-ref" className="font-display text-2xl font-bold text-primary tracking-wide mt-1">{d.ref_number}</p>
          </div>
          <Row icon={d.urgency === "Urgent" ? <AlertTriangle className="h-5 w-5 text-destructive" /> : <Clock className="h-5 w-5 text-primary" />}
            label={t("urgency")} value={urgencyLabel} testId="confirmation-urgency"
            valueClass={d.urgency === "Urgent" ? "text-destructive font-bold" : "text-primary font-semibold"} />
          <Row icon={<ClipboardList className="h-5 w-5 text-primary" />} label={t("type_of_work")} value={tWork(d.work_type, lang)} testId="confirmation-worktype" />
          {d.vehicle && (
            <>
              <Row icon={<ClipboardList className="h-5 w-5 text-primary" />} label={t("mobile_service_badge")} value={t("mobile_service_badge")} testId="confirmation-mobile-badge" />
              <Row icon={<ClipboardList className="h-5 w-5 text-primary" />} label={t("plate_label")} value={d.vehicle.plate} testId="confirmation-plate" />
              <Row icon={<ClipboardList className="h-5 w-5 text-primary" />} label={t("fuel_type")} value={t(`fuel_${d.vehicle.fuel_type}`)} testId="confirmation-fuel" />
              {d.vehicle.odometer_km && <Row icon={<ClipboardList className="h-5 w-5 text-primary" />} label={t("odometer_display")} value={`${d.vehicle.odometer_km} km`} testId="confirmation-odometer" />}
              <Row icon={<ClipboardList className="h-5 w-5 text-primary" />} label={t("spark_quote_requested")} value={d.vehicle.spark_plug_quote ? t("yes") : t("no")} testId="confirmation-spark" />
            </>
          )}
          <Row icon={<Clock className="h-5 w-5 text-primary" />} label={t("preferred_time")} value={tCompletion(d.preferred_time, lang)} testId="confirmation-time" />
          {d.media_count > 0 && (
            <Row icon={<ClipboardList className="h-5 w-5 text-primary" />} label={t("uploaded_files")} value={t("files_count", { n: d.media_count })} testId="confirmation-media" />
          )}
        </div>

        <div className="mt-8 space-y-3">
          <button data-testid="confirmation-new-btn" onClick={() => nav("/urgency")} className="btn-primary">
            {t("submit_another")}
          </button>
          <button data-testid="confirmation-myrequests-btn" onClick={() => nav("/my-requests")}
            className="flex w-full items-center justify-center gap-2 rounded-full border border-border py-3 text-sm font-semibold text-primary hover:bg-secondary transition-colors">
            <ClipboardList className="h-4 w-4" /> {t("my_requests")}
          </button>
          <button data-testid="confirmation-home-btn" onClick={() => nav("/home")}
            className="flex w-full items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
            <Home className="h-4 w-4" /> {t("back_home")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ icon, label, value, valueClass = "text-foreground font-semibold", testId }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
        <p data-testid={testId} className={`mt-0.5 ${valueClass}`}>{value}</p>
      </div>
    </div>
  );
}
